import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Helper to resolve relative /api/uploads URLs to absolute URLs for <img> tags
export const resolveAssetUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cpd_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('cpd_token');
      localStorage.removeItem('cpd_admin');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Admin management APIs (owner only for mutations)
export const adminAPI = {
  list: () => api.get('/admins'),
  create: (data) => api.post('/admins', data),
  update: (adminId, data) => api.patch(`/admins/${adminId}`, data),
  delete: (adminId) => api.delete(`/admins/${adminId}`),
  // Owner-only: download a fresh gzip'd mongodump of the database
  downloadBackup: () => api.post('/admin/backup/download', null, {
    responseType: 'blob',
    timeout: 120000, // mongodump + tar.gz can take up to 2 min
  }),
};

// Branding APIs
export const brandingAPI = {
  get: () => api.get('/branding'),
  update: (data) => api.patch('/branding', data),
  getPublic: () => axios.get(`${API_BASE}/public/branding`),
  getEmailStatus: () => api.get('/branding/email-status'),
};

// Event APIs
export const eventAPI = {
  create: (data) => api.post('/events', data),
  list: () => api.get('/events'),
  get: (eventId) => api.get(`/events/${eventId}`),
  update: (eventId, data) => api.patch(`/events/${eventId}`, data),
  delete: (eventId) => api.delete(`/events/${eventId}`),
  duplicate: (eventId) => api.post(`/events/${eventId}/duplicate`),
  getPublic: (inviteCode) => axios.get(`${API_BASE}/public/events/${inviteCode}`),
  getReport: (eventId) => api.get(`/events/${eventId}/report`),
  getAudit: (eventId) => api.get(`/events/${eventId}/audit`),
  sendReminders: (eventId) => api.post(`/events/${eventId}/send-reminders`, null, { timeout: 120000 }),
  pushCertificates: (eventId) => api.post(`/events/${eventId}/push-certificates`, null, { timeout: 180000 }),
};

// Attendee APIs
export const attendeeAPI = {
  register: (inviteCode, data) => axios.post(`${API_BASE}/public/events/${inviteCode}/register`, data),
  list: (eventId) => api.get(`/events/${eventId}/attendees`),
  checkIn: (attendeeId, data) => api.patch(`/attendees/${attendeeId}/checkin`, data),
  checkOut: (attendeeId) => api.patch(`/attendees/${attendeeId}/checkout`),
  getByEmail: (eventId, email) => api.get(`/attendees/by-email?event_id=${eventId}&email=${email}`),
  downloadCertificate: (attendeeId) => api.get(`/attendees/${attendeeId}/certificate`, { responseType: 'blob' }),
  downloadCertificatePublic: (attendeeId) => axios.get(`${API_BASE}/public/attendees/${attendeeId}/certificate`, { responseType: 'blob' }),
  sendCertificate: (attendeeId) => api.post(`/attendees/${attendeeId}/send-certificate`),
  sendCertificateCustom: (attendeeId, email) => api.post(`/attendees/${attendeeId}/send-certificate-custom`, { email }),
  bulkDownloadCertificates: (eventId) => api.get(`/events/${eventId}/certificates/bulk`, { responseType: 'blob' }),
  canDownload: (attendeeId) => axios.get(`${API_BASE}/public/attendees/${attendeeId}/can-download`),
};

// Feedback APIs
export const feedbackAPI = {
  submit: (eventId, data) => axios.post(`${API_BASE}/events/${eventId}/feedback`, data),
  list: (eventId) => api.get(`/events/${eventId}/feedback`),
  bulkEmail: (eventId, data) => api.post(`/events/${eventId}/feedback/bulk-email`, data || {}),
};

// RSVP Reconciliation (cross-check WhatsApp list vs registered attendees)
export const rsvpReconcileAPI = {
  reconcile: (eventId, data) => api.post(`/events/${eventId}/rsvp-reconcile`, data),
  getState: (eventId) => api.get(`/events/${eventId}/rsvp-reconcile/state`),
  saveState: (eventId, rows) => api.put(`/events/${eventId}/rsvp-reconcile/state`, { rows }),
  clearState: (eventId) => api.delete(`/events/${eventId}/rsvp-reconcile/state`),
  addWalkin: (eventId, data) => api.post(`/events/${eventId}/rsvp-walkin`, data),
};

// Sponsor APIs
export const sponsorAPI = {
  create: (data) => api.post('/sponsors', data),
  list: () => api.get('/sponsors'),
  get: (sponsorId) => api.get(`/sponsors/${sponsorId}`),
  update: (sponsorId, data) => api.patch(`/sponsors/${sponsorId}`, data),
  delete: (sponsorId) => api.delete(`/sponsors/${sponsorId}`),
  downloadCertificate: (sponsorId, eventId) => api.get(`/sponsors/${sponsorId}/certificate/${eventId}`, { responseType: 'blob' }),
  getEventSponsors: (eventId) => api.get(`/events/${eventId}/sponsors`),
};

// Sponsorship Tier APIs removed — tiers are now fully per-event (no global list).

// Special Guest Invitations (Chief Guest / Guest of Honor / Special Guest invites)
export const specialGuestAPI = {
  list: (eventId) => api.get(`/events/${eventId}/special-guest-invitations`),
  create: (eventId, data) => api.post(`/events/${eventId}/special-guest-invitations`, data),
  update: (invitationId, data) => api.patch(`/special-guest-invitations/${invitationId}`, data),
  delete: (invitationId) => api.delete(`/special-guest-invitations/${invitationId}`),
  preview: (invitationId) => api.get(`/special-guest-invitations/${invitationId}/preview`),
  downloadPdf: (invitationId) => api.get(`/special-guest-invitations/${invitationId}/pdf`, { responseType: 'blob' }),
  sendEmail: (invitationId, data) => api.post(`/special-guest-invitations/${invitationId}/send`, data || {}),
};

// Public Sponsorship Prospectus + EOI submission (no auth required)
export const publicProspectusAPI = {
  get: (inviteCode) => axios.get(`${API_BASE}/public/events/${inviteCode}/prospectus`),
  submitEOI: (inviteCode, data) =>
    axios.post(`${API_BASE}/public/events/${inviteCode}/sponsorship-eoi`, data),
};

// Admin EOI moderation + tax invoice flow
export const eoiAPI = {
  list: (eventId) => api.get(`/events/${eventId}/sponsorship-eois`),
  update: (eoiId, data) => api.patch(`/sponsorship-eois/${eoiId}`, data),
  delete: (eoiId) => api.delete(`/sponsorship-eois/${eoiId}`),
  approve: (eoiId) => api.post(`/sponsorship-eois/${eoiId}/approve`),
  paymentReceived: (eoiId) => api.post(`/sponsorship-eois/${eoiId}/payment-received`),
  revertPayment: (eoiId) => api.post(`/sponsorship-eois/${eoiId}/payment-revert`),
  downloadInvoice: (eoiId) => api.get(`/sponsorship-eois/${eoiId}/invoice.pdf`, { responseType: 'blob' }),
  downloadLetter: (eoiId) => api.get(`/sponsorship-eois/${eoiId}/letter.pdf`, { responseType: 'blob' }),
  emailInvoice: (eoiId) => api.post(`/sponsorship-eois/${eoiId}/email-invoice`),
  thankAllSponsors: (eventId) => api.post(`/events/${eventId}/thank-sponsors`),
};

// Global audit log
export const auditAPI = {
  list: (params = {}) => api.get('/audit-logs', { params }),
  filters: () => api.get('/audit-logs/filters'),
};

// Member sponsorship (annual member contributions)
export const memberSponsorshipAPI = {
  list: (params = {}) => api.get('/member-contributions', { params }),
  update: (id, data) => api.patch(`/member-contributions/${id}`, data),
  delete: (id) => api.delete(`/member-contributions/${id}`),
  emailInvoice: (id) => api.post(`/member-contributions/${id}/email-invoice`),
  downloadInvoice: async (id, memberName = 'member') => {
    try {
      const res = await api.get(`/member-contributions/${id}/invoice.pdf`, { responseType: 'blob' });
      _downloadBlob(res.data, `tax_invoice_${memberName.replace(/\s+/g, '_')}.pdf`);
    } catch (e) { throw await _decodeBlobError(e); }
  },
};

// Public member sponsorship submission — no auth, uses fresh axios so it doesn't 401 unauth users
export const publicMemberSponsorshipSubmit = (data) =>
  axios.post(`${API_BASE}/public/member-sponsorship`, data);

// Helper: trigger a browser file download from a blob response
const _downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Helper: read an error response whose responseType was 'blob' and try to parse the JSON
// inside so callers can show a meaningful message.
const _decodeBlobError = async (error) => {
  try {
    const blob = error?.response?.data;
    if (blob && typeof blob.text === 'function') {
      const text = await blob.text();
      try {
        const parsed = JSON.parse(text);
        if (parsed?.detail) {
          // Mutate so existing `e.response.data.detail` lookups keep working
          error.response.data = parsed;
        }
      } catch (_) {
        // Not JSON — leave as-is
      }
    }
  } catch (_) {
    // best-effort
  }
  return error;
};

// Authenticated exports (CSV / admin ICS / sponsorship prospectus PDF)
export const exportAPI = {
  attendeesCsv: async (eventId, eventTitle = 'event') => {
    try {
      const res = await api.get(`/events/${eventId}/attendees.csv`, { responseType: 'blob' });
      _downloadBlob(res.data, `attendees_${eventTitle.replace(/\s+/g, '_')}.csv`);
    } catch (e) { throw await _decodeBlobError(e); }
  },
  feedbackCsv: async (eventId, eventTitle = 'event') => {
    try {
      const res = await api.get(`/events/${eventId}/feedback.csv`, { responseType: 'blob' });
      _downloadBlob(res.data, `feedback_${eventTitle.replace(/\s+/g, '_')}.csv`);
    } catch (e) { throw await _decodeBlobError(e); }
  },
  adminEventIcs: async (eventId, eventTitle = 'event') => {
    try {
      const res = await api.get(`/events/${eventId}/calendar.ics`, { responseType: 'blob' });
      _downloadBlob(res.data, `${eventTitle.replace(/\s+/g, '_')}.ics`);
    } catch (e) { throw await _decodeBlobError(e); }
  },
  sponsorshipProspectusPdf: async (eventId, eventTitle = 'event') => {
    try {
      const res = await api.get(`/events/${eventId}/sponsorship-prospectus.pdf`, { responseType: 'blob' });
      _downloadBlob(res.data, `sponsorship_prospectus_${eventTitle.replace(/\s+/g, '_')}.pdf`);
    } catch (e) { throw await _decodeBlobError(e); }
  },
};

// Public ICS — direct link is fine (no auth)
export const publicEventIcsUrl = (inviteCode) => `${API_BASE}/public/events/${inviteCode}/calendar.ics`;

// Upload APIs
export const uploadAPI = {
  logo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/uploads/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Stats API
export const statsAPI = {
  get: () => api.get('/stats'),
  analytics: () => api.get('/analytics'),
};

export default api;
